import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, FormLabel, List, ListItem, Modal, ModalClose,
  ModalDialog, Option, Select, Stack, Typography,
} from '@mui/joy';
import { Globe, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { ImportableWebsitePage, ImportFromWebsiteResponse } from '../../types/marketing';

interface ImportWebsitePagesModalProps {
  open: boolean;
  onClose: () => void;
  onImported: (result: ImportFromWebsiteResponse) => void;
}

const PAGE_GROUPS: { label: string; match: (page: ImportableWebsitePage) => boolean }[] = [
  { label: 'Service Pages', match: (page) => page.page_type === 'service_detail' },
  { label: 'Location Pages', match: (page) => page.page_type === 'area_served' },
  { label: 'Other Pages', match: () => true },
];

const isPreselected = (page: ImportableWebsitePage) =>
  (page.page_type === 'service_detail' || page.page_type === 'area_served') && !page.already_imported;

export const ImportWebsitePagesModal: React.FC<ImportWebsitePagesModalProps> = ({
  open, onClose, onImported,
}) => {
  const navigate = useNavigate();
  // null = let the server pick its default project; set only by the Select.
  const [projectId, setProjectId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [initializedFor, setInitializedFor] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['website-pages-for-import', projectId],
    queryFn: async () => {
      const response = await api.getWebsitePagesForImport(projectId ?? undefined);
      return response.data;
    },
    enabled: open,
  });

  const activeProjectId = projectId ?? data?.selected_project_id ?? null;

  // Pre-select service + location pages whenever a project's list first loads.
  useEffect(() => {
    if (!open) {
      setProjectId(null);
      setSelected(new Set());
      setInitializedFor(null);
      setSubmitError(null);
      return;
    }
    if (data && activeProjectId !== null && activeProjectId !== initializedFor) {
      setSelected(new Set(data.pages.filter(isPreselected).map((page) => page.id)));
      setInitializedFor(activeProjectId);
    }
  }, [open, data, activeProjectId, initializedFor]);

  const togglePage = (pageId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  };

  const toggleGroup = (pages: ImportableWebsitePage[], allSelected: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      pages.forEach((page) => {
        if (allSelected) {
          next.delete(page.id);
        } else if (!page.already_imported) {
          next.add(page.id);
        }
      });
      return next;
    });
  };

  const handleImport = async () => {
    if (!activeProjectId || selected.size === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const response = await api.importWebsitePages({
        project_id: activeProjectId,
        page_ids: Array.from(selected),
      });
      onImported(response.data);
      onClose();
    } catch (err) {
      const message = (err as any)?.responseData?.error
        || (err instanceof Error ? err.message : 'Import failed. Please try again.');
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Split pages into display groups (first matching group wins).
  const grouped: { label: string; pages: ImportableWebsitePage[] }[] = [];
  if (data) {
    const claimed = new Set<number>();
    PAGE_GROUPS.forEach(({ label, match }) => {
      const pages = data.pages.filter((page) => !claimed.has(page.id) && match(page));
      pages.forEach((page) => claimed.add(page.id));
      if (pages.length > 0) {
        grouped.push({ label, pages });
      }
    });
  }

  const projectsWithPages = data?.projects.filter((project) => project.pages_count > 0) ?? [];
  const hasNoProjects = !!data && data.projects.length === 0;
  const hasNoPages = !!data && data.projects.length > 0 && data.pages.length === 0;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: '90vw', width: 640, maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <DialogTitle>
          <Globe style={{ marginRight: 8 }} />
          Import From Website
        </DialogTitle>
        <Divider />
        <DialogContent>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && !!error && (
            <Alert color="danger" sx={{ my: 2 }}>
              <Box>
                <Typography level="title-sm">Couldn't load your website pages.</Typography>
                <Button variant="outlined" color="danger" size="sm" sx={{ mt: 1 }} onClick={() => refetch()}>
                  Retry
                </Button>
              </Box>
            </Alert>
          )}

          {!isLoading && !error && hasNoProjects && (
            <Stack spacing={2} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
              <Typography level="h4">No website yet</Typography>
              <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 420 }}>
                Generate a website with ApexOS first — then you can import its service and
                location pages here to build topical maps automatically.
              </Typography>
              <Button
                variant="solid"
                color="primary"
                endDecorator={<ArrowRight />}
                onClick={() => navigate('/marketing/website-builder/new')}
              >
                Set Up Your Website
              </Button>
            </Stack>
          )}

          {!isLoading && !error && hasNoPages && (
            <Stack spacing={2} alignItems="center" sx={{ py: 4, textAlign: 'center' }}>
              <Typography level="h4">Your website has no pages yet</Typography>
              <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 420 }}>
                Finish generating your website first, then come back to import its pages.
              </Typography>
              <Button
                variant="solid"
                color="primary"
                endDecorator={<ArrowRight />}
                onClick={() => navigate('/marketing/website-builder')}
              >
                Go To Website Builder
              </Button>
            </Stack>
          )}

          {!isLoading && !error && data && data.pages.length > 0 && (
            <Stack spacing={2}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Select the pages to build topical maps for. ApexOS will automatically choose
                the 3 main keywords each page should rank for, then generate blog ideas.
              </Typography>

              {projectsWithPages.length > 1 && (
                <FormControl size="sm" sx={{ maxWidth: 320 }}>
                  <FormLabel>Website</FormLabel>
                  <Select
                    value={activeProjectId}
                    onChange={(_, value) => {
                      if (value !== null && value !== activeProjectId) {
                        setProjectId(value as number);
                      }
                    }}
                  >
                    {projectsWithPages.map((project) => (
                      <Option key={project.id} value={project.id}>
                        {project.name} ({project.pages_count} pages)
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ maxHeight: '55vh', overflow: 'auto' }}>
                {grouped.map(({ label, pages }) => {
                  const importable = pages.filter((page) => !page.already_imported);
                  const selectedCount = importable.filter((page) => selected.has(page.id)).length;
                  const allSelected = importable.length > 0 && selectedCount === importable.length;
                  return (
                    <Box key={label} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selectedCount > 0 && !allSelected}
                          disabled={importable.length === 0}
                          onChange={() => toggleGroup(pages, allSelected)}
                          label={label}
                          sx={{ fontWeight: 'lg' }}
                        />
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          {selectedCount}/{pages.length}
                        </Typography>
                      </Box>
                      <List size="sm" sx={{ '--ListItem-paddingY': '4px' }}>
                        {pages.map((page) => (
                          <ListItem key={page.id}>
                            <Checkbox
                              checked={selected.has(page.id)}
                              disabled={page.already_imported}
                              onChange={() => togglePage(page.id)}
                              sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1, ml: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>
                                  {page.title}
                                </Typography>
                                {page.already_imported && (
                                  <Chip variant="soft" color="success" size="sm">
                                    Imported
                                  </Chip>
                                )}
                              </Box>
                              <Typography level="body-xs" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
                                {page.url}
                              </Typography>
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              </Box>

              {submitError && (
                <Alert color="danger">{submitError}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        {!isLoading && !error && data && data.pages.length > 0 && (
          <DialogActions>
            <Button
              variant="solid"
              color="primary"
              loading={submitting}
              disabled={selected.size === 0}
              onClick={handleImport}
            >
              Import {selected.size} Page{selected.size === 1 ? '' : 's'}
            </Button>
            <Button variant="plain" color="neutral" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </DialogActions>
        )}
      </ModalDialog>
    </Modal>
  );
};
